import React, { useState, useEffect } from 'react';
import { dashboardAPI } from '../lib/api';
import { 
  AlertTriangle,
  AlertCircle,
  Bell,
  ChevronRight,
  User
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const Alerts = () => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const fetchAlerts = async () => {
      try {
        const response = await dashboardAPI.getAlerts();
        setAlerts(response.data);
      } catch (error) {
        console.error('Error fetching alerts:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchAlerts();
  }, []);

  const filteredAlerts = alerts.filter(a => {
    if (filter === 'all') return true;
    return a.severity === filter;
  });

  const criticalCount = alerts.filter(a => a.severity === 'critical').length;
  const warningCount = alerts.filter(a => a.severity === 'warning').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-[#331062] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="alerts-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-800 font-outfit">
          Alerts & Notifications
        </h1>
        <p className="text-slate-500 mt-1">
          Monitor performance issues and escalations
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilter('all')}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Alerts</p>
                <p className="text-3xl font-bold text-slate-800 mt-2 font-outfit">{alerts.length}</p>
              </div>
              <div className="p-3 bg-slate-100 rounded-xl">
                <Bell className="w-6 h-6 text-slate-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`cursor-pointer hover:shadow-md transition-shadow ${filter === 'critical' ? 'ring-2 ring-red-500' : ''}`}
          onClick={() => setFilter('critical')}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Critical</p>
                <p className="text-3xl font-bold text-red-600 mt-2 font-outfit">{criticalCount}</p>
              </div>
              <div className="p-3 bg-red-50 rounded-xl">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`cursor-pointer hover:shadow-md transition-shadow ${filter === 'warning' ? 'ring-2 ring-amber-500' : ''}`}
          onClick={() => setFilter('warning')}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Warnings</p>
                <p className="text-3xl font-bold text-amber-600 mt-2 font-outfit">{warningCount}</p>
              </div>
              <div className="p-3 bg-amber-50 rounded-xl">
                <AlertCircle className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Alerts List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-outfit flex items-center gap-2">
              <Bell className="w-5 h-5 text-[#331062]" />
              {filter === 'all' ? 'All Alerts' : filter === 'critical' ? 'Critical Alerts' : 'Warnings'}
            </CardTitle>
            {filter !== 'all' && (
              <button
                onClick={() => setFilter('all')}
                className="text-sm text-[#331062] hover:underline"
                data-testid="clear-filter-btn"
              >
                Clear filter
              </button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {filteredAlerts.length === 0 ? (
            <div className="text-center py-12">
              <Bell className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-800">No alerts</h3>
              <p className="text-slate-500 mt-1">
                {filter === 'all' 
                  ? 'Your team is performing well!' 
                  : `No ${filter} alerts at this time`
                }
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <AnimatePresence>
                {filteredAlerts.map((alert, index) => (
                  <motion.div
                    key={alert.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ delay: index * 0.05 }}
                    className={`alert-card ${alert.severity}`}
                    data-testid={`alert-item-${alert.id}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`p-2 rounded-lg ${
                        alert.severity === 'critical' ? 'bg-red-100' : 'bg-amber-100'
                      }`}>
                        {alert.severity === 'critical' ? (
                          <AlertTriangle className={`w-5 h-5 text-red-600`} />
                        ) : (
                          <AlertCircle className={`w-5 h-5 text-amber-600`} />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-medium text-slate-800">{alert.designer_name}</h4>
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                            alert.severity === 'critical' 
                              ? 'bg-red-100 text-red-700' 
                              : 'bg-amber-100 text-amber-700'
                          }`}>
                            {alert.severity}
                          </span>
                        </div>
                        <p className="text-sm text-slate-600">{alert.message}</p>
                        <div className="flex items-center gap-4 mt-3">
                          <span className="text-xs text-slate-400 capitalize">
                            {alert.alert_type.replace('_', ' ')}
                          </span>
                          <Link
                            to={`/performance/${alert.designer_id}`}
                            className="text-xs text-[#331062] font-medium hover:underline flex items-center gap-1"
                            data-testid={`alert-view-${alert.id}`}
                          >
                            View Performance <ChevronRight className="w-3 h-3" />
                          </Link>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Alerts;
